// @spec AI-STR-031
'use client';

import { useUser } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { MessageSquareCode, ShieldAlert } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/skeleton';
import {
  PromptTestPanel,
  PromptVersionHistory,
  SystemPromptsEditor,
  SystemPromptsList,
  useActivateSystemPrompt,
  useCreateSystemPrompt,
  useDeleteSystemPrompt,
  useSystemPrompt,
  useSystemPromptHistory,
  useSystemPrompts,
  useTestSystemPrompt,
  useUpdateSystemPrompt,
} from '@/features/admin';
import type { PromptTestResult, SystemPromptContext, SystemPromptUpdate } from '@/features/admin';

export const Route = createFileRoute('/_authenticated/admin/system-prompts')({
  component: SystemPromptsPage,
});

/**
 * Check if user has Super Admin role from Clerk metadata.
 */
function isSuperAdmin(user: ReturnType<typeof useUser>['user']): boolean {
  if (!user) return false;

  const publicMetadata = user.publicMetadata as Record<string, unknown> | undefined;
  return publicMetadata?.role === 'super_admin';
}

function SystemPromptsPage() {
  const { user, isLoaded: userLoaded } = useUser();
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  // State
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showTestPanel, setShowTestPanel] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Check Super Admin access
  const hasAccess = isSuperAdmin(user);

  // Fetch prompts list (active only)
  const { data: promptsData, isLoading: isLoadingList, error: listError } = useSystemPrompts();

  // Fetch selected prompt details
  const { data: selectedPrompt, isLoading: isLoadingPrompt } = useSystemPrompt(
    selectedPromptId && !isCreatingNew ? selectedPromptId : null
  );

  // Fetch version history for selected prompt's context
  const { data: historyData, isLoading: isLoadingHistory } = useSystemPromptHistory(
    showHistory && selectedPrompt ? selectedPrompt.context : null
  );

  // Mutations
  const { mutateAsync: createPrompt, isPending: isCreating } = useCreateSystemPrompt();
  const { mutateAsync: updatePrompt, isPending: isUpdating } = useUpdateSystemPrompt();
  const { mutateAsync: activatePrompt } = useActivateSystemPrompt();
  const { mutateAsync: deletePrompt } = useDeleteSystemPrompt();
  const { mutateAsync: testPrompt, isPending: isTesting } = useTestSystemPrompt();

  const clearMessages = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => {
      setSuccessMessage(null);
    }, 5000);
  };

  const handleSelectPrompt = useCallback((id: string) => {
    clearMessages();
    setSelectedPromptId(id);
    setIsCreatingNew(false);
  }, []);

  const handleCreateNew = useCallback(() => {
    clearMessages();
    setSelectedPromptId(null);
    setIsCreatingNew(true);
  }, []);

  const handleSave = useCallback(
    async (data: SystemPromptUpdate & { context?: SystemPromptContext }) => {
      clearMessages();

      try {
        if (isCreatingNew) {
          // Create new prompt
          const newPrompt = await createPrompt({
            name: data.name ?? '',
            context: data.context ?? 'general',
            prompt: data.prompt ?? '',
          });
          setSelectedPromptId(newPrompt.id);
          setIsCreatingNew(false);
          showSuccess('System prompt created successfully.');
        } else if (selectedPromptId) {
          // Update existing prompt - only include defined fields
          const updateData: SystemPromptUpdate = {};
          if (data.name !== undefined) updateData.name = data.name;
          if (data.prompt !== undefined) updateData.prompt = data.prompt;

          const updated = await updatePrompt({
            id: selectedPromptId,
            data: updateData,
          });
          // If version changed, select the new version
          if (updated.id !== selectedPromptId) {
            setSelectedPromptId(updated.id);
          }
          showSuccess('System prompt updated successfully.');
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to save prompt';
        setErrorMessage(message);
      }
    },
    [isCreatingNew, selectedPromptId, createPrompt, updatePrompt]
  );

  const handleTest = useCallback(
    async (testMessage: string): Promise<PromptTestResult> => {
      if (!selectedPrompt) {
        return { success: false, error: 'No prompt selected' };
      }

      try {
        const result = await testPrompt({
          prompt: selectedPrompt.prompt,
          testMessage,
        });
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Test failed';
        return { success: false, error: message };
      }
    },
    [selectedPrompt, testPrompt]
  );

  const handleActivate = useCallback(
    async (id: string) => {
      setActivatingId(id);
      clearMessages();

      try {
        await activatePrompt(id);
        showSuccess('Prompt version activated successfully.');
        setSelectedPromptId(id);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to activate version';
        setErrorMessage(message);
      } finally {
        setActivatingId(null);
      }
    },
    [activatePrompt]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      setDeletingId(id);
      clearMessages();

      try {
        await deletePrompt(id);
        showSuccess('Prompt version deleted.');
        // If we deleted the selected prompt, clear selection
        if (id === selectedPromptId) {
          setSelectedPromptId(null);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to delete version';
        setErrorMessage(message);
      } finally {
        setDeletingId(null);
      }
    },
    [deletePrompt, selectedPromptId]
  );

  const handleViewVersion = useCallback((id: string) => {
    setShowHistory(false);
    setSelectedPromptId(id);
    setIsCreatingNew(false);
  }, []);

  // Show loading while checking user
  if (!userLoaded) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 bg-muted rounded" />
            <div className="h-4 w-64 bg-muted rounded" />
          </div>
        </div>
      </div>
    );
  }

  // Show 403 if not Super Admin
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-lg text-center">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-destructive/10 p-4">
              <ShieldAlert className="h-12 w-12 text-destructive" />
            </div>
          </div>
          <h1 className="text-2xl font-semibold text-foreground mb-2">{t('accessDenied.title')}</h1>
          <p className="text-muted-foreground mb-6">
            {t('accessDenied.superAdminRequired')}
          </p>
          <button
            type="button"
            className="text-primary-600 hover:underline"
            onClick={() => {
              void navigate({ to: '/dashboard' });
            }}
          >
            {t('accessDenied.returnToDashboard')}
          </button>
        </div>
      </div>
    );
  }

  // Show error state
  if (listError) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl">
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4">
            <p className="text-destructive">Failed to load system prompts. Please try again.</p>
          </div>
        </div>
      </div>
    );
  }

  // Show loading skeleton
  if (isLoadingList) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl space-y-6">
          {/* Header skeleton */}
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-64" />
            </div>
          </div>

          {/* Content skeleton */}
          <div className="grid gap-6 lg:grid-cols-[300px_1fr]">
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
            <Skeleton className="h-96 w-full" />
          </div>
        </div>
      </div>
    );
  }

  const prompts = promptsData?.prompts ?? [];

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        {/* Page header */}
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary-100 p-2 dark:bg-primary-900">
            <MessageSquareCode className="h-6 w-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">System Prompts</h1>
            <p className="text-sm text-muted-foreground">
              Manage AI assistant behavior across different modules
            </p>
          </div>
        </div>

        {/* Success message */}
        {successMessage && (
          <div className="rounded-md border border-green-500/50 bg-green-500/10 p-4">
            <p className="text-sm text-green-700 dark:text-green-400">{successMessage}</p>
          </div>
        )}

        {/* Error message */}
        {errorMessage && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4">
            <p className="text-sm text-destructive">{errorMessage}</p>
          </div>
        )}

        {/* Main content */}
        <div className="grid gap-6 lg:grid-cols-[300px_1fr]">
          {/* Prompts list */}
          <div className="lg:border-r lg:pr-6">
            <SystemPromptsList
              prompts={prompts}
              selectedId={selectedPromptId}
              onSelect={handleSelectPrompt}
              onCreateNew={handleCreateNew}
              isLoading={isLoadingList}
            />
          </div>

          {/* Editor */}
          <div>
            {selectedPromptId || isCreatingNew ? (
              <SystemPromptsEditor
                prompt={selectedPrompt ?? null}
                onSave={handleSave}
                onTest={() => {
                  setShowTestPanel(true);
                }}
                onShowHistory={() => {
                  setShowHistory(true);
                }}
                isLoading={isCreating || isUpdating || isLoadingPrompt}
                isTesting={isTesting}
                isNew={isCreatingNew}
              />
            ) : (
              <div className="flex h-96 items-center justify-center rounded-lg border border-dashed">
                <div className="text-center">
                  <MessageSquareCode className="mx-auto h-12 w-12 text-muted-foreground/50" />
                  <p className="mt-4 text-muted-foreground">
                    Select a prompt to edit or create a new one.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Version History Dialog */}
        <PromptVersionHistory
          isOpen={showHistory}
          onClose={() => {
            setShowHistory(false);
          }}
          versions={historyData?.versions ?? []}
          onActivate={handleActivate}
          onView={handleViewVersion}
          onDelete={handleDelete}
          isLoading={isLoadingHistory}
          activatingId={activatingId}
          deletingId={deletingId}
        />

        {/* Test Panel Dialog */}
        {selectedPrompt && (
          <PromptTestPanel
            isOpen={showTestPanel}
            onClose={() => {
              setShowTestPanel(false);
            }}
            promptName={selectedPrompt.name}
            promptText={selectedPrompt.prompt}
            onTest={handleTest}
            isTesting={isTesting}
          />
        )}
      </div>
    </div>
  );
}
